import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useMediaQuery } from "react-responsive";
import { useMapboxKey } from "@/components/maps/useMapboxKey";
import { useDispatch, useSelector } from "react-redux";
import { addPin, addStateLines, removeLocationPoints, removePins, removePolyline, setMaxBounds } from "@/utils/map";
import { MarketPin, PickupPin, PredictionDeliveryPin } from "@/components/common/svgs";
import { useQuery } from "@/utils/query";
import { ErrorComponent } from "@/components/common/errors";
import { validateTrailerType } from "@/utils/validation";
import { clearLocations, getDeliveryLocation, getPickupLocation } from "@/state/searchSlice";
import debounce from "lodash/debounce";

export const PredictionLoadRatesMap = ({ filteredMarkets }) => {
    const mapRef = useRef(null);
    const mapContainer = useRef(null);
    const isDesktop = useMediaQuery({
        query: "(min-width: 1025px)",
    });
    const { pickupLocation, deliveryLocation, pickupMarketCenter, deliveryMarketCenter } = useSelector((state) => state.search);
    const routeInfo = useSelector((state) => state.loadRates.marketToMarketRoute);
    const mapboxAccessKey = useMapboxKey(true);
    const [mapIsLoaded, setMapIsLoaded] = useState(false);
    const [styleIsLoaded, setStyleIsLoaded] = useState(false);
    const [error, setError] = useState(false);
    const dispatch = useDispatch();
    const [redraw, setRedraw] = useState(0);
    const [marketCenters, setMarketCenters] = useState(null);

    const { pickupZip, deliveryZip, trailerType } = useQuery();

    const addLocationPins = () => {
        if (mapRef?.current && pickupLocation.data && deliveryLocation.data && filteredMarkets !== null && marketCenters) {
            const map = mapRef.current;
            const startPoint = [pickupLocation.data.longitude, pickupLocation.data.latitude];
            const endPoint = [deliveryLocation.data.longitude, deliveryLocation.data.latitude];
            const bounds = new mapboxgl.LngLatBounds(startPoint, endPoint);
            const padding = { top: 110, bottom: 50, left: isDesktop ? 775 : 100, right: 50 };
            setMaxBounds(map);
            addPin(map, PickupPin, startPoint, -17);
            addPin(map, PredictionDeliveryPin, endPoint, -17);

            if (filteredMarkets && marketCenters) {
                filteredMarkets.forEach((market, index) => {
                    const marketCenter = marketCenters[market.zip];
                    if (marketCenter) {
                        const point = [marketCenter.longitude, marketCenter.latitude];
                        bounds.extend(point);
                        addPin(
                            map,
                            () => {
                                return <MarketPin number={index + 1} />;
                            },
                            point,
                            -13,
                        );
                    }
                });
            }
            map.fitBounds(bounds, { padding, duration: 1000 });
        }
    };

    const addPolyLine = () => {
        if (mapRef?.current && routeInfo.data?.polyline?.length && pickupLocation.data && deliveryLocation.data) {
            const startPoint = [pickupLocation.data.longitude, pickupLocation.data.latitude];
            const endPoint = [deliveryLocation.data.longitude, deliveryLocation.data.latitude];

            const map = mapRef.current;
            try {
                map.addSource("routeLine", {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [
                            {
                                type: "Feature",
                                geometry: {
                                    type: "LineString",
                                    coordinates: [startPoint, ...routeInfo.data.polyline, endPoint],
                                },
                            },
                        ],
                    },
                });
                map.addLayer({
                    id: "routeLine",
                    type: "line",
                    source: "routeLine",
                    paint: {
                        "line-width": 2,
                        "line-color": "#43BC98",
                    },
                });
            } catch (err) {
                console.log("Error adding polyline", err);
            }
        }
    };

    const debouncedResize = debounce(() => {
        setRedraw(Math.random());
    }, 500);

    useEffect(() => {
        if (mapContainer.current && mapboxAccessKey) {
            mapboxgl.accessToken = mapboxAccessKey;
            const currentMap = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/lioneightdev/cm4zecvtw009w01sr1ytv71xi",
                center: [-98.5795, 37.8283],
                zoom: isDesktop ? 2.6 : 2.15,
                maxZoom: 8,
                minZoom: isDesktop ? 2.6 : 2.15,
                dragRotate: false,
                touchZoomRotate: false,
                pitchWithRotate: false,
                fitBoundsOptions: { duration: 400 },
            });
            mapRef.current = currentMap;

            currentMap.on("load", async () => {
                await addStateLines(currentMap);
                setMapIsLoaded(true);
                if (!marketCenters) {
                    const marketsResponse = await fetch("/market-centers.json");
                    const centers = await marketsResponse.json();
                    if (centers) {
                        setMarketCenters(centers);
                    }
                }
            });

            currentMap.on("style.load", async () => {
                setStyleIsLoaded(true);
            });

            currentMap.on("error", async () => {
                setError(true);
            });

            currentMap.on("resize", () => {
                debouncedResize();
            });

            return () => {
                removePins();
                currentMap.remove();
            };
        }
    }, [mapboxAccessKey]);

    useEffect(() => {
        if (mapIsLoaded && styleIsLoaded) {
            const map = mapRef.current;
            if (pickupLocation.data && deliveryLocation.data) {
                removeLocationPoints(map);
                addLocationPins();
                if (routeInfo.data) {
                    removePolyline(map);
                    addPolyLine();
                }
            }
        }
    }, [
        routeInfo.data,
        pickupLocation.data,
        deliveryLocation.data,
        pickupMarketCenter.data,
        deliveryMarketCenter.data,
        mapRef?.current,
        mapIsLoaded,
        styleIsLoaded,
        redraw,
        filteredMarkets,
        marketCenters,
    ]);

    useEffect(() => {
        if (pickupLocation.error || deliveryLocation.error || routeInfo.error || pickupMarketCenter.error || deliveryMarketCenter.error) {
            setError(true);
        }
    }, [pickupLocation.error, deliveryLocation.error, routeInfo.error]);

    useEffect(() => {
        if (!trailerType || !pickupZip || !deliveryZip) return;
        if (!validateTrailerType(trailerType) || isNaN(pickupZip) || isNaN(deliveryZip)) return;

        dispatch(getPickupLocation({ zip: pickupZip }));
        dispatch(getDeliveryLocation({ zip: deliveryZip }));
    }, [pickupZip, deliveryZip]);

    useEffect(() => {
        return () => {
            dispatch(clearLocations());
        };
    }, []);

    return (
        <div className="w-full min-h-[397px] h-[397px] flex">
            {error ? (
                <ErrorComponent className={`bg-cm-p-surface h-[397px]`} />
            ) : (
                <div
                    ref={mapContainer}
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        width: "100%",
                        maxHeight: "397px",
                    }}
                />
            )}
        </div>
    );
};
